import React, { useState, useEffect } from 'react';
import { Plus, Trash2, MessageCircle, Edit3, Check, X, ChevronLeft, AlertTriangle, HelpCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ChatSession } from '../../types/chat';


interface ChatSessionListProps {
  sessions: ChatSession[];
  activeSessionId: string;
  onSessionSelect: (sessionId: string) => void;
  onNewSession: () => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, newTitle: string) => void;
  onClearAllSessions?: () => void;
  onToggleCollapse: () => void;
}

const ChatSessionList = ({
  sessions,
  activeSessionId,
  onSessionSelect,
  onNewSession,
  onDeleteSession,
  onRenameSession,
  onClearAllSessions,
  onToggleCollapse,
}: ChatSessionListProps) => {
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const { isAdmin } = useAuth();
  
  // Update timestamps every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every 60 seconds

    return () => clearInterval(interval);
  }, []);
  
  const startEditing = (sessionId: string, currentTitle: string) => {
    setEditingSessionId(sessionId);
    setEditTitle(currentTitle);
  };

  const cancelEditing = () => {
    setEditingSessionId(null);
    setEditTitle('');
  };

  const saveTitle = () => {
    if (editingSessionId && editTitle.trim()) {
      onRenameSession(editingSessionId, editTitle.trim());
    }
    cancelEditing();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveTitle();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleDeleteClick = (session: ChatSession) => {
    onDeleteSession(session.id);
  };
  const formatTime = (date: Date) => {
    const diffInSeconds = Math.floor((currentTime.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Gerade eben';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? 'vor 1 Min' : `vor ${diffInMinutes} Min`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return diffInHours === 1 ? 'vor 1 Std' : `vor ${diffInHours} Std`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    return diffInDays === 1 ? 'vor 1 Tag' : `vor ${diffInDays} Tagen`;
  };

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="p-3 border-b border-border flex items-center gap-2">
        <Button
          onClick={onNewSession}
          size="sm"
          className="flex-1 bg-primary hover:bg-primary/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Chat
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
          className="p-2 hover:bg-muted/50"
          title="Seitenleiste einklappen"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        {sessions.map((session) => (
          <div
            key={session.id}
            className={`px-3 py-3 hover:bg-muted/50 transition-colors ${
              activeSessionId === session.id ? 'bg-green-50 dark:bg-green-950/30 border-l-2 border-green-600' : ''
            }`}
          >
            <div className="flex items-start justify-between">
              <div 
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => editingSessionId !== session.id && onSessionSelect(session.id)}
              >
                  {editingSessionId === session.id ? (
                    <div className="flex items-center space-x-2">
                      <Input
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={handleKeyPress}
                        className="text-sm h-8"
                        autoFocus
                        onBlur={saveTitle}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={saveTitle}
                        className="p-1 h-auto"
                      >
                        <Check className="w-3 h-3 text-primary" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={cancelEditing}
                        className="p-1 h-auto"
                      >
                        <X className="w-3 h-3 text-muted-foreground" />
                      </Button>
                    </div>
                  ) : (
                    <div className="group flex items-center justify-between">
                      <h3 className="font-medium text-sm text-foreground truncate">
                        {session.title}
                      </h3>
                      {isAdmin && (
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditing(session.id, session.title);
                            }}
                            className="p-1 h-auto text-muted-foreground hover:text-muted-foreground hover:bg-muted/50"
                          >
                            <Edit3 className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(session);
                            }}
                            className="p-1 h-auto text-muted-foreground hover:text-muted-foreground hover:bg-muted/50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatTime(session.lastMessage)}
                  </p>
                </div>
            </div>
          </div>
        ))}
      </div>

      {/* Help and Clear All Sessions Buttons at bottom */}
      {sessions.length > 0 && onClearAllSessions && (
        <div className="p-3 border-t border-border space-y-2">
          {/* Help Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-950/30"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Hilfe benötigt?
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-blue-600" />
                  Benötigen Sie Hilfe?
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-4">
                    <p>Für Unterstützung wenden Sie sich bitte an:</p>
                    <div className="bg-muted/50 p-4 rounded-lg border border-border space-y-3">
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                        <div className="space-y-1">
                          <p className="font-semibold text-foreground">Johann Sawatzky</p>
                          <a
                            href="mailto:johann.sawatzky@de.lrmed.com"
                            className="text-blue-600 hover:text-blue-700 underline break-all"
                          >
                            johann.sawatzky@de.lrmed.com
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                        <div className="space-y-1">
                          <p className="font-semibold text-foreground">Christian Wasem</p>
                          <a
                            href="mailto:christian.wasem@de.lrmed.com"
                            className="text-blue-600 hover:text-blue-700 underline break-all"
                          >
                            christian.wasem@de.lrmed.com
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Schließen</AlertDialogCancel>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Clear All Sessions Button - Admin Only */}
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-black hover:bg-destructive/10 hover:text-destructive"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Alle Sitzungen löschen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                    Alle Sitzungen löschen
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Diese Aktion wird alle {sessions.length} Chat-Sitzungen und deren Gesprächsverlauf permanent löschen.
                    Dies kann nicht rückgängig gemacht werden.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onClearAllSessions}
                    className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                  >
                    Alle Sitzungen löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}

    </div>
  );
};

export default ChatSessionList;